import { Feature, SnackbarSeverityType } from '../../../enums';
import { SchoolConfig, FeatureConfigs } from '../../../types/schoolConfigType';
import {
  ModerationSettings,
  SchoolConfigModerationFeature
} from '../types/moderationTypes';
import _ from 'lodash';
import { isValidEmail, normalizeSkipForEmails } from './utils';

const getModerationFeature = (featureConfigs?: {
  [key in Feature]?: FeatureConfigs;
}): SchoolConfigModerationFeature | undefined => {
  return featureConfigs?.[Feature.POST_MODERATION];
};

const buildSettingsFromFeature = (
  feature?: SchoolConfigModerationFeature
): ModerationSettings => ({
  postModerationEnabled: Boolean(feature?.enabled ?? true),
  disabledEmailsCsv: (feature?.skipFor || []).join(', ')
});

const getInvalidEmails = (csv: string): string[] => {
  const normalizedEmails = normalizeSkipForEmails(csv);
  return normalizedEmails.filter((email) => !isValidEmail(email));
};

const buildUpdatedFeatureConfigs = (
  existingData: Partial<Record<Feature, FeatureConfigs>>,
  settings: ModerationSettings
): { [key in Feature]: FeatureConfigs } => {
  const normalizedEmails = normalizeSkipForEmails(settings.disabledEmailsCsv);
  return {
    ...(existingData as any),
    [Feature.POST_MODERATION]: {
      enabled: settings.postModerationEnabled,
      skipFor: _.compact(normalizedEmails)
    }
  };
};

const processSchoolConfig = (config?: SchoolConfig) => {
  const featureConfigs = config?.featureConfigs;
  const moderationFeature = getModerationFeature(featureConfigs);
  const settings = buildSettingsFromFeature(moderationFeature);
  return { featureConfigs: featureConfigs || {}, moderationFeature, settings };
};

const toastWarningIfMissingModeration = (
  moderationFeature: SchoolConfigModerationFeature | undefined,
  showToast?: (message: string, severity: SnackbarSeverityType) => void
) => {
  if (!moderationFeature) {
    showToast?.(
      'Post moderation configuration not found for this school.',
      SnackbarSeverityType.warning
    );
  }
};

export {
  getModerationFeature,
  buildSettingsFromFeature,
  getInvalidEmails,
  buildUpdatedFeatureConfigs,
  processSchoolConfig,
  toastWarningIfMissingModeration
};
